import { toStorageUrl } from "@sajilni/utils/image-url";

export type CompanyTranslationLike = {
  name?: string | null;
  bio?: string | null;
  tags?: string[];
  language?: { code?: string | null } | null;
};

export type CompanyProfileLike = {
  id?: string | null;
  eventId?: string | null;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  countryCode?: string | null;
  jobTitle?: string | null;
  company?: string | null;
  country?: string | null;
  city?: string | null;
  state?: string | null;
  gender?: string | null;
  age?: number | null;
  bio?: string | null;
  image?: string | null;
  socialMedia?: unknown;
  userId?: string | null;
  extraData?: unknown;
  createdAt?: Date;
  updatedAt?: Date;
};

export type CompanyTeamMemberLike = {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  profiles?: CompanyProfileLike[];
};

export type CompanyRecordLike = {
  id: string;
  email?: string | null;
  phone?: string | null;
  logo?: string | null;
  website?: string | null;
  facebook?: string | null;
  linkedin?: string | null;
  x?: string | null;
  instagram?: string | null;
  location?: string | null;
  boothName?: string | null;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  translations?: CompanyTranslationLike[];
  networking?: unknown[] | unknown | null;
  team?: CompanyTeamMemberLike[];
};

export type CompanyGroupLike = {
  type: "EXHIBITORS" | "SPONSORS";
  color?: string | null;
  translations?: Array<{ name?: string | null }>;
};

export type CompanyParticipantItem = {
  tags: Array<{ id: string; name: string }>;
  participant: {
    _id: string;
    name: string;
    email: string | null;
    status: unknown;
    isAcceptMeetings: unknown;
    createdAt: Date;
    updatedAt: Date;
    lastLoginAt: unknown;
    active: boolean;
  };
  profile: {
    id: string | null;
    name: string;
    email: string | null;
    phoneNumber: string | null;
    countryCode: string | null;
    jobTitle: string | null;
    company: string;
    country: string | null;
    city: string | null;
    state: string | null;
    gender: string | null;
    age: number | null;
    bio: string | null;
    image: string | null;
    socialMedia: unknown;
    userId: string | null;
    extraData: Record<string, unknown>;
    createdAt: Date;
    updatedAt: Date;
  };
  id: string;
  groupType: "EXHIBITORS" | "SPONSORS";
  groupName: Array<{ name?: string | null }>;
  color: string | null;
  connectionRequests: unknown;
  networking: unknown;
  isBookmarked: boolean;
  logo?: string | null;
  team?: CompanyTeamMember[];
  products?: CompanyProductItem[];
  documents?: CompanyDocumentItem[];
  videos?: CompanyVideoItem[];
};

export type CompanyTeamMember = {
  id: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  image: string | null;
};

export type CompanyProductItem = {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  link: string | null;
};

export type CompanyDocumentItem = {
  id: string;
  name: string;
  url: string;
  type: string | null;
};

export type CompanyVideoItem = {
  id: string;
  name: string;
  url: string;
};

export type LocalizedMediaTranslation = {
  name?: string | null;
  description?: string | null;
  url?: string | null;
  language?: { code?: string | null } | null;
};

const firstNonEmpty = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export function pickCompanyTranslation(
  translations: CompanyTranslationLike[] | undefined,
  lang?: string
) {
  if (!translations?.length) return undefined;
  if (lang) {
    const match = translations.find(
      (translation) => translation.language?.code === lang
    );
    if (match) return match;
  }
  return translations[0];
}

export function pickCompanyContactProfile(
  team: CompanyTeamMemberLike[] | undefined,
  companyEmail?: string | null,
  eventId?: string
) {
  if (!team?.length) return undefined;
  const contact =
    team.find(
      (member) =>
        member.email &&
        companyEmail &&
        member.email.toLowerCase() === companyEmail.toLowerCase()
    ) ?? team[0];
  if (!contact?.profiles?.length) return undefined;
  if (eventId) {
    return (
      contact.profiles.find((profile) => profile.eventId === eventId) ??
      contact.profiles[0]
    );
  }
  return contact.profiles[0];
}

export function mapCompanyToParticipantItem({
  company,
  group,
  profile,
  lang,
  connectionRequests = null,
  isBookmarked = false,
}: {
  company: CompanyRecordLike;
  group: CompanyGroupLike;
  profile?: CompanyProfileLike;
  lang?: string;
  connectionRequests?: unknown;
  isBookmarked?: boolean;
}): CompanyParticipantItem {
  const translation = pickCompanyTranslation(company.translations, lang);
  const card = companyCardSearchFields({
    companyEmail: company.email,
    translations: company.translations,
    profile,
    lang,
  });
  const companyName = card.name;
  const tagNames =
    (translation?.tags?.length ? translation.tags : company.tags)?.filter(
      Boolean
    ) ?? [];

  const extraData =
    profile?.extraData && typeof profile.extraData === "object"
      ? { ...(profile.extraData as Record<string, unknown>) }
      : {};

  const networking = Array.isArray(company.networking)
    ? company.networking[0] ?? null
    : company.networking ?? null;

  return {
    tags: tagNames.map((name) => ({ id: name, name })),
    participant: {
      _id: company.id,
      name: companyName,
      email: card.email,
      status: undefined,
      isAcceptMeetings: undefined,
      createdAt: company.createdAt,
      updatedAt: company.updatedAt,
      lastLoginAt: undefined,
      active: true,
    },
    profile: {
      id: profile?.id ?? null,
      name: companyName,
      email: card.email,
      phoneNumber: company.phone ?? profile?.phoneNumber ?? null,
      countryCode: profile?.countryCode ?? null,
      jobTitle: profile?.jobTitle ?? null,
      company: companyName,
      country: profile?.country ?? null,
      city: profile?.city ?? company.location ?? null,
      state: profile?.state ?? null,
      gender: profile?.gender ?? null,
      age: profile?.age ?? null,
      bio: profile?.bio ?? translation?.bio ?? null,
      image: company.logo ?? profile?.image ?? null,
      socialMedia: profile?.socialMedia ?? {
        website: company.website ?? null,
        facebook: company.facebook ?? null,
        linkedin: company.linkedin ?? null,
        twitter: company.x ?? null,
        instagram: company.instagram ?? null,
      },
      userId: profile?.userId ?? null,
      extraData: {
        ...extraData,
        boothName: company.boothName ?? extraData.boothName ?? null,
        location: company.location ?? extraData.location ?? null,
      },
      createdAt: profile?.createdAt ?? company.createdAt,
      updatedAt: profile?.updatedAt ?? company.updatedAt,
    },
    id: company.id,
    groupType: group.type,
    groupName: group.translations ?? [],
    color: group.color ?? null,
    connectionRequests,
    networking,
    isBookmarked: Boolean(isBookmarked),
    logo: company.logo ?? null,
  };
}

/**
 * The two strings `companyMatchesSearch` can hit on a mapped card: the
 * resolved company name, and the company email with a contact-profile fallback.
 */
export function companyCardSearchFields({
  companyEmail,
  translations,
  profile,
  lang,
}: {
  companyEmail?: string | null;
  translations?: CompanyTranslationLike[];
  profile?: {
    company?: string | null;
    name?: string | null;
    email?: string | null;
  };
  lang?: string;
}) {
  const translation = pickCompanyTranslation(translations, lang);
  return {
    name: firstNonEmpty(
      translation?.name,
      profile?.company,
      profile?.name,
      companyEmail
    ),
    email: companyEmail ?? profile?.email ?? null,
  };
}

export function companyRecordMatchesSearch({
  companyEmail,
  translations,
  profile,
  lang,
  query,
}: {
  companyEmail?: string | null;
  translations?: CompanyTranslationLike[];
  profile?: {
    company?: string | null;
    name?: string | null;
    email?: string | null;
  };
  lang?: string;
  query?: string;
}) {
  const safeQuery = query?.trim().toLowerCase();
  if (!safeQuery) return true;
  const card = companyCardSearchFields({
    companyEmail,
    translations,
    profile,
    lang,
  });
  return [card.name, card.email].some((value) =>
    value?.toLowerCase().includes(safeQuery)
  );
}

/**
 * True when a match can still depend on the contact profile. Companies that
 * already match on the translation name or company email, and companies whose
 * name and email are both set, do not need a team read.
 */
/** Stored on `searchCards` when the requested language has no translation. */
export const COMPANY_SEARCH_FALLBACK_LANG = "*";

export function companySearchCardText(
  name: string,
  email?: string | null
): string {
  return `${name}\n${email ?? ""}`.toLowerCase();
}

/**
 * True when the card name or email can still come from the contact profile.
 * Companies with a translation name and a company email do not.
 */
export function companySearchCardNeedsProfile(
  email?: string | null,
  translations?: CompanyTranslationLike[]
) {
  if (!firstNonEmpty(email)) return true;
  if (!translations?.length) return true;
  return translations.some((translation) => !firstNonEmpty(translation.name));
}

/**
 * One lowercased name+email blob per translation language, plus the
 * first-translation fallback. `boothName` is not a search field.
 *
 * `translations` must already be in fallback order (translation id ascending),
 * matching `pickCompanyTranslation`.
 */
export function buildCompanySearchCards({
  email,
  translations,
  profile,
}: {
  email?: string | null;
  translations?: CompanyTranslationLike[];
  profile?: {
    company?: string | null;
    name?: string | null;
    email?: string | null;
  };
}): Array<{ lang: string; text: string }> {
  const ordered = translations ?? [];
  const seen = new Set<string>();
  const cards: Array<{ lang: string; text: string }> = [];

  for (const translation of ordered) {
    const code = translation.language?.code?.trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const card = companyCardSearchFields({
      companyEmail: email,
      translations: [translation],
      profile,
      lang: code,
    });
    cards.push({
      lang: code,
      text: companySearchCardText(card.name, card.email),
    });
  }

  const fallback = companyCardSearchFields({
    companyEmail: email,
    translations: ordered,
    profile,
  });
  cards.push({
    lang: COMPANY_SEARCH_FALLBACK_LANG,
    text: companySearchCardText(fallback.name, fallback.email),
  });
  return cards;
}

export function companySearchNeedsContact({
  companyEmail,
  translations,
  lang,
  query,
}: {
  companyEmail?: string | null;
  translations?: CompanyTranslationLike[];
  lang?: string;
  query?: string;
}) {
  const safeQuery = query?.trim().toLowerCase();
  if (!safeQuery) return false;
  const card = companyCardSearchFields({
    companyEmail,
    translations,
    lang,
  });
  if (card.name.toLowerCase().includes(safeQuery)) return false;
  if (card.email?.toLowerCase().includes(safeQuery)) return false;
  const translation = pickCompanyTranslation(translations, lang);
  return !firstNonEmpty(translation?.name) || !firstNonEmpty(companyEmail);
}

export function companyMatchesSearch(
  item: CompanyParticipantItem,
  query?: string
) {
  const safeQuery = query?.trim().toLowerCase();
  if (!safeQuery) return true;
  return [
    item.participant.name,
    item.participant.email,
    item.profile.name,
    item.profile.company,
    item.profile.email,
  ].some((value) => value?.toLowerCase().includes(safeQuery));
}

export function paginateCompanyItems<T>(
  items: T[],
  page: number = 1,
  take: number = 10
) {
  const currentPage = Number.isFinite(page) && page > 0 ? page : 1;
  const pageSize = Number.isFinite(take) && take > 0 ? take : 10;
  const total = items.length;
  const start = (currentPage - 1) * pageSize;
  return {
    data: items.slice(start, start + pageSize),
    pagination: {
      total,
      hasNextPage: currentPage * pageSize < total,
      hasPreviousPage: (currentPage - 1) * pageSize > 0,
      currentPage,
    },
  };
}

export function pickLocalizedTranslation<T extends LocalizedMediaTranslation>(
  translations: T[] | undefined,
  lang?: string
): T | undefined {
  if (!translations?.length) return undefined;
  if (lang) {
    const match = translations.find(
      (translation) => translation.language?.code === lang
    );
    if (match) return match;
  }
  return translations[0];
}

export function mapCompanyTeamMembers(
  team: CompanyTeamMemberLike[] | undefined,
  eventId?: string
): CompanyTeamMember[] {
  if (!team?.length) return [];
  return team
    .filter((member): member is CompanyTeamMemberLike & { id: string } =>
      Boolean(member.id)
    )
    .map((member) => {
      const profile = eventId
        ? member.profiles?.find((item) => item.eventId === eventId) ??
          member.profiles?.[0]
        : member.profiles?.[0];
      return {
        id: member.id,
        name: firstNonEmpty(profile?.name, member.name, member.email),
        jobTitle: profile?.jobTitle ?? null,
        email: member.email ?? null,
        image: profile?.image ?? member.image ?? null,
      };
    });
}

export function mapCompanyProducts(
  products: Array<{
    id: string;
    image?: string | null;
    link?: string | null;
    translations?: LocalizedMediaTranslation[];
  }>,
  lang?: string
): CompanyProductItem[] {
  return products.map((product) => {
    const translation = pickLocalizedTranslation(product.translations, lang);
    return {
      id: product.id,
      name: firstNonEmpty(translation?.name),
      description: translation?.description ?? null,
      image: product.image ?? null,
      link: product.link ?? null,
    };
  });
}

export function mapCompanyDocuments(
  documents: Array<{
    id: string;
    type?: string | null;
    translations?: LocalizedMediaTranslation[];
  }>,
  lang?: string
): CompanyDocumentItem[] {
  return documents.flatMap((document) => {
    const translation = pickLocalizedTranslation(document.translations, lang);
    const url = translation?.url?.trim();
    if (!url) return [];
    return [
      {
        id: document.id,
        name: firstNonEmpty(translation?.name),
        url,
        type: document.type ?? null,
      },
    ];
  });
}

export function mapCompanyVideos(
  videos: Array<{
    id: string;
    translations?: LocalizedMediaTranslation[];
  }>,
  lang?: string
): CompanyVideoItem[] {
  return videos.flatMap((video) => {
    const translation = pickLocalizedTranslation(video.translations, lang);
    const url = translation?.url?.trim();
    if (!url) return [];
    return [
      {
        id: video.id,
        name: firstNonEmpty(translation?.name),
        url,
      },
    ];
  });
}

const ABSOLUTE_OR_EMBEDDED = /^(https?:\/\/|data:|blob:)/i;

export function toAbsoluteMediaUrl(
  url: string | null | undefined,
  origin?: string
): string | null {
  if (!url?.trim()) return null;
  const normalized = toStorageUrl(url.trim()) ?? url.trim();
  if (ABSOLUTE_OR_EMBEDDED.test(normalized)) return normalized;

  const base = (origin || "").replace(/\/+$/, "");
  if (!base) return normalized;
  return `${base}${normalized.startsWith("/") ? normalized : `/${normalized}`}`;
}

export function withAbsoluteCompanyMedia<T extends CompanyParticipantItem>(
  item: T,
  origin?: string
): T {
  return {
    ...item,
    logo: toAbsoluteMediaUrl(item.logo ?? item.profile.image, origin),
    profile: {
      ...item.profile,
      image: toAbsoluteMediaUrl(item.profile.image, origin),
    },
    team: item.team?.map((member) => ({
      ...member,
      image: toAbsoluteMediaUrl(member.image, origin),
    })),
    products: item.products?.map((product) => ({
      ...product,
      image: toAbsoluteMediaUrl(product.image, origin),
    })),
    documents: item.documents?.map((document) => ({
      ...document,
      url: toAbsoluteMediaUrl(document.url, origin) ?? document.url,
    })),
    videos: item.videos?.map((video) => ({
      ...video,
      url: toAbsoluteMediaUrl(video.url, origin) ?? video.url,
    })),
  };
}
